import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Button,
  Slider,
  FormGroup,
  Grid,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  Modal,
} from '@mui/material';
import { ThumbsUpDownOutlined } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { keyframes } from '@emotion/react'; // Import keyframes from Emotion
import { getDecodedToken, postUserFeedback } from 'service/service';


const primaryModules: string[] = []

const updateSelectedModules = (
  checked: boolean,
  currentValues: string[],
  targetModule: string
): string[] => {
  if (checked) {
    return [...currentValues, targetModule];
  }
  return currentValues.filter((val) => val !== targetModule);
};

const RatingSlider = ({ label, name, control }: { label: string, name: string, control: any }) => (
  <FormControl fullWidth sx={{ mb: 3 }}>
    <FormLabel color='primary'>{label}</FormLabel>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Box sx={{ px: 1 }}>
          <Slider
            {...field}
            valueLabelDisplay="auto"
            step={1}
            marks
            min={1}
            max={5}
          />
        </Box>
      )}
    />
  </FormControl>
);

const PepWiseFeedbackForm = () => {
  const user = getDecodedToken()
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { control, handleSubmit, formState: { isSubmitting } } = useForm({
    defaultValues: {
      // A. About You
      role: '',
      usageFrequency: '',
      primaryModules: primaryModules,
      
      // B. Overall Experience
      overallSatisfaction: 3,
      navigationComparison: '',
      visualClarity: 3,
      performance: 3,
      likes: '',
      improvements: '',
      
      // C. Feature Deep-Dive
      // Login & Home
      findInThreeClicks: '',
      timeToFirstTask: '',
      missingTiles: '',
      
      // My Favorites
      useFavorites: '',
      pinEase: 3,
      reducesClicks: '',
      neededOptions: '',
      
      // Optimized Landing Pages
      landingPageActions: 3,
      clicksToTask: '',
      aboveTheFold: '',
      
      // Intelligent Search
      usedSearch: '',
      searchRelevancy: 3,
      searchSpeed: 3,
      missingSearchFeatures: '',
      
      // Light Theme
      readabilityComparison: '',
      eyeStrain: '',
      lowContrastAreas: '',
      
      // D. Quality & Reliability
      bugsEncountered: '',
      blocksWork: '',
      timeSavingImprovement: ''
    }
  });

  const onSubmit = (data: any) => {
    // Console.log all form field values
    const payload = {
      feedback: data,
      userEmail: user.email,
      user: user,
      location: globalThis.location.href,
      timestamp: new Date().toISOString() 
    }
    console.log(payload)
    postUserFeedback(payload).then((res:any) => {
      console.log(res)
      // Show the thank you modal
      setIsModalOpen(true);
    }).catch((err:any) => {
      console.error(err)
    })
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    const path = sessionStorage.getItem('currentPath')
    if(path && !path?.includes('/feedback')){
      // navigate(path)
    }
  };

  const modules = ['IBP', 'Mosaic', 'XOps', 'FinOps', 'ADB', 'Data Estate', 'Other'];
  const roles = ['Exec', 'Ops', 'Engineer', 'Analyst', 'Other'];
  const frequencyOptions = ['Daily', 'Weekly', 'Monthly', 'First time'];

  return (
    <>
      <Box sx={{ 
        paddingLeft: '5rem !important',
        paddingRight: '5rem !important',
        paddingTop:'100px !important',
        margin: '0 auto',
        p: 3,
        color: '#003771 !important',
        "& .MuiFormControlLabel-root .MuiFormControlLabel-label":{
          color: 'gray !important',
        },
        "& .Mui-checked":{
          color: '#003771 !important',
        },
        "& .MuiSvgIcon-root":{
          color: '#003771!important',
        },
        "& .MuiInputBase-root":{
          color: 'inherit !important',
        },
        "& .MuiInputLabel-root":{
          color: 'gray !important',
        },
        '& .MuiOutlinedInput-root': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#003771',
          },
          '&:focus .MuiOutlinedInput-notchedOutline': {
            borderColor: '#003771',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#39ACE7',
          },
        },
        }}>
        {/* Header */}
        <Box textAlign="center" sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            We would love to hear from you!
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Your input directly shapes the next releases.
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Phase 1 focused on standardization & simplification. 
          </Typography>
          {/*<Typography variant="subtitle1" color="text.secondary">
            Next: module-level re-imagination. Thank you for sharing concise, candid feedback.
          </Typography> */}
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Section A: About You */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              About You
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Stack direction="row" spacing={2}>
            {/* Role/Persona */}
            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend">Your role/persona</FormLabel>
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field}>
                    {roles.map((role) => (
                      <FormControlLabel 
                        key={role} 
                        value={role} 
                        control={<Radio />} 
                        label={role}
                        sx={{color: '#003771 !important'}}
                      />
                    ))}
                  </RadioGroup>
                )}
              />
            </FormControl>

            {/* Usage Frequency */}
            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend">Usage frequency</FormLabel>
              <Controller
                name="usageFrequency"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field}>
                    {frequencyOptions.map((freq) => (
                      <FormControlLabel 
                        key={freq} 
                        value={freq} 
                        control={<Radio />} 
                        label={freq}
                      />
                    ))}
                  </RadioGroup>
                )}
              />
            </FormControl>

            {/* Primary Modules */}
            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend">Primary modules you use (multi-select)</FormLabel>
              <Controller
                name="primaryModules"
                control={control}
                render={({ field }) => (
                  <FormGroup>
                    {modules.map((module) => (
                      <FormControlLabel
                        key={module}
                        control={
                          <Checkbox
                            checked={field?.value?.includes(module)}
                            onChange={(e) => {
                              field.onChange(updateSelectedModules(e.target.checked, field.value, module));
                            }}
                          />
                        }
                        label={module}
                      />
                    ))}
                  </FormGroup>
                )}
              />
            </FormControl>
            </Stack>
          </Box>

          {/* Section B: Overall Experience */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              Overall Experience
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={4}>
              {/* Left Column - Rating Sliders */}
              <Grid item xs={12} md={6}>
                <RatingSlider 
                  label="Overall satisfaction [1–5]" 
                  name="overallSatisfaction" 
                  control={control} 
                />
                <RatingSlider 
                  label="Visual clarity/readability [1–5]" 
                  name="visualClarity" 
                  control={control} 
                />
                <RatingSlider 
                  label="Performance/speed [1–5]" 
                  name="performance" 
                  control={control} 
                />
              </Grid>

              {/* Right Column - Navigation and Text Fields */}
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
                  <FormLabel component="legend">Easier to navigate vs older version?</FormLabel>
                  <Controller
                    name="navigationComparison"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        {['Better', 'Same', 'Worse'].map((option) => (
                          <FormControlLabel 
                            key={option} 
                            value={option} 
                            control={<Radio />} 
                            label={option}
                          />
                        ))}
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                    
                <Controller
                  name="likes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Top 2 things you liked"
                      fullWidth
                      multiline
                      rows={2}
                      sx={{ mb: 2 }}
                      variant="outlined"
                      color="secondary" 
                    />
                  )}
                />

                <Controller
                  name="improvements"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Top 2 things to improve next"
                      fullWidth
                      multiline
                      rows={2}
                      variant="outlined"
                      color="secondary" 
                    />
                  )}
                />
              </Grid>            
            </Grid>
          </Box>

          {/* Section C: Feature Deep-Dive (Phase-1) */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              Feature Deep-Dive (Phase-1)
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {/* Login & Home Subsection */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2, fontWeight: 600 }}>
              Login & Home (redesign)
            </Typography>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Find what you need in &lt;3 clicks?</FormLabel>
                  <Controller
                    name="findInThreeClicks"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field} row>
                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Time to first task improved?</FormLabel>
                  <Controller
                    name="timeToFirstTask"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field} row>
                        {['Yes', 'Same', 'Worse'].map((option) => (
                          <FormControlLabel key={option} value={option} control={<Radio />} label={option} />
                        ))}
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Controller
              name="missingTiles"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="What tile/shortcut is missing?"
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ mb: 3 }}
                  variant="outlined"
                  color="secondary" 
                />
              )}
            />

            <Divider sx={{ my: 3 }} />

            {/* My Favorites Subsection */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
              My Favorites (pin/access)
            </Typography>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Do you use Favorites?</FormLabel>
                  <Controller
                    name="useFavorites"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <RatingSlider 
                  label="Pin/unpin ease [1–5]" 
                  name="pinEase" 
                  control={control} 
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Does it reduce clicks?</FormLabel>
                  <Controller
                    name="reducesClicks"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        {['Yes', 'Somewhat', 'No'].map((option) => (
                          <FormControlLabel key={option} value={option} control={<Radio />} label={option} />
                        ))}
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Controller
              name="neededOptions"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Needed options (reorder, share, bulk-pin)?"
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ mb: 3 }}
                  variant="outlined"
                  color="secondary" 
                />
              )}
            />

            <Divider sx={{ my: 3 }} />

            {/* Optimized Landing Pages Subsection */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
              Optimized Landing Pages
            </Typography>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <RatingSlider 
                  label="Landing page shows the right 'first actions'? [1–5]" 
                  name="landingPageActions" 
                  control={control} 
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Avg. clicks to your key task</FormLabel>
                  <Controller
                    name="clicksToTask"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        <FormControlLabel value="1" control={<Radio />} label="1" />
                        <FormControlLabel value="2" control={<Radio />} label="2" />
                        <FormControlLabel value="3+" control={<Radio />} label="3+" />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Controller
              name="aboveTheFold"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="What should appear above the fold?"
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ mb: 3 }}
                  variant="outlined"
                  color="secondary" 
                />
              )}
            />

            <Divider sx={{ my: 3 }} />

            {/* Intelligent Search Subsection */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
              Intelligent Search (field-level + hovers)
            </Typography>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Used search this week?</FormLabel>
                  <Controller
                    name="usedSearch"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field} row>
                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <RatingSlider 
                  label="Relevancy/accuracy [1–5]" 
                  name="searchRelevancy" 
                  control={control} 
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RatingSlider 
                  label="Speed [1–5]" 
                  name="searchSpeed" 
                  control={control} 
                />
              </Grid>
            </Grid>

            <Controller
              name="missingSearchFeatures"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Missing fields/filters or actions from results (open, copy link, export)?"
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ mb: 3 }}
                  variant="outlined"
                  color="secondary" 
                />
              )}
            />

            <Divider sx={{ my: 3 }} />

            {/* Light Theme Subsection */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
              Light Theme
            </Typography>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Readability vs dark</FormLabel>
                  <Controller
                    name="readabilityComparison"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        <FormControlLabel value="Better" control={<Radio />} label="Better" />
                        <FormControlLabel value="Same" control={<Radio />} label="Same" />
                        <FormControlLabel value="Worse" control={<Radio />} label="Worse" />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Eye strain after 30+ mins</FormLabel>
                  <Controller
                    name="eyeStrain"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        <FormControlLabel value="Lower" control={<Radio />} label="Lower" />
                        <FormControlLabel value="Same" control={<Radio />} label="Same" />
                        <FormControlLabel value="Higher" control={<Radio />} label="Higher" />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Controller
              name="lowContrastAreas"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Any low-contrast areas to fix?"
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ mb: 3 }}
                  variant="outlined"
                  color="secondary" 
                />
              )}
            />
          </Box>

          {/* Section D: Quality & Reliability */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              Quality & Reliability
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Controller
              name="bugsEncountered"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Any bugs/errors encountered?"
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                  variant="outlined"
                  color="secondary" 
                />
              )}
            />

            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">If yes, do they block work?</FormLabel>
              <Controller
                name="blocksWork"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="No" control={<Radio />} label="No" />
                  </RadioGroup>
                )}
              />
            </FormControl>

            <Controller
              name="timeSavingImprovement"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="One improvement that would save you the most time next month"
                  fullWidth
                  multiline
                  rows={2}
                  variant="outlined"
                  color="secondary" 
                />
              )}
            />
          </Box>

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button 
              type="submit" 
              variant="contained"
              disabled={isSubmitting}
              size="large"
              sx={{ px: 4, bgcolor: '#003771 !important', borderRadius: '8px !important' }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </Box>
        </form>
      </Box>

      {/* Thank You Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="thank-you-modal"
        aria-describedby="thank-you-message"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',

        }}
      >
        <Box
          sx={{
            padding: 4,
            maxWidth: 500,
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: 24,
            backgroundColor: '#f5f5f5',
          }}
        >
          <Typography variant="h4" component="h2" gutterBottom sx={{ color: '#003771', fontWeight: 600 }}>
            Thank You!
          </Typography>
          <Typography variant="h6" sx={{ mb: 2, color: '#003771' }}>
            Your Feedback Has Been Received
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            Thank you for taking the time to share your valuable feedback about PepWise 2.0. 
            Your insights will directly help us shape the next releases and improve the user experience.
          </Typography>
          <Button
            variant="contained"
            onClick={handleCloseModal}
            sx={{
              background: '#003771',
              color: 'white',
              px: 4,
              '&:hover': {
                background: '#002952',
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Modal>
    </>
  );
};

// 1. Define the Keyframes for the Pulse Animation
const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 rgba(0, 0, 0, 0.4);
  }
  50% {
    transform: scale(1.5);
    box-shadow: 0 0 10px rgba(57, 172, 231, 0.8); /* Use the icon color for the glow */
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 rgba(0, 0, 0, 0.4);
  }
`;

// Define animation for pulse, bounce
const pulseBounce = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  25% {
    transform: translateY(-18px) scale(1.05); /* Bounce up and slightly scale */
  }
  50% {
    transform: translateY(0) scale(1); /* Return to base, scale normal */
  }
  75% {
    transform: translateY(-14px) scale(1.02); /* Smaller bounce up and scale */
  }
`;

// Define a combined animation for pulse, bounce, and glow
const pulseBounceGlow = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1);
    box-shadow: 0 0 0 rgba(57, 172, 231, 0.4);
  }
  25% {
    transform: translateY(-18px) scale(1.05); /* Bounce up and slightly scale */
    box-shadow: 0 0 10px rgba(57, 172, 231, 0.8); /* Glow */
  }
  50% {
    transform: translateY(0) scale(1); /* Return to base, scale normal */
    box-shadow: 0 0 0 rgba(57, 172, 231, 0.4); /* Glow fades */
  }
  75% {
    transform: translateY(-14px) scale(1.02); /* Smaller bounce up and scale */
    box-shadow: 0 0 5px rgba(57, 172, 231, 0.6); /* Moderate glow */
  }
`;

export const FeedbackLink = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const existingPath = sessionStorage.getItem('navigatedRoute');
  
  // Use a simple click handler or a framework-specific Link component if you 
  // had a router (e.g., <NavLink> or <Link> from react-router-dom)
  const handleClick = () => {
    // In a real application, you would use a router hook here:
    navigate('/pepwisev2/feedback');
  };
  const [hasRouteChanged, setHasRouteChanged] = useState(false);
	const [previousPath, setPreviousPath] = useState(existingPath);
  useEffect(() => {
    console.log(location.pathname);
    console.log(previousPath);
		// Check if the current path is different from the previous path
		if (location.pathname !== previousPath && !location?.pathname?.includes('/feedback')) {
			// A route change has occurred
      sessionStorage.setItem('navigatedRoute', location.pathname);
			console.log('Route changed from:', previousPath, 'to:', location.pathname);

			// Alter the boolean state
			setHasRouteChanged(true);

			// flip it back after a delay,
			const timer = setTimeout(() => {
				setHasRouteChanged(false);
			}, 7000);
			return () => clearTimeout(timer); // Cleanup function

		}

		// 4. Update the previous path state for the next comparison
		setPreviousPath(location.pathname);

	}, [location.pathname, previousPath]); // Dependency Array: Run effect when pathname changes

  return (
    // 1. Tooltip provides the hover text
    <Tooltip title="Feedback" placement="right">
      {/* 2. IconButton is the correct MUI component for a clickable icon */}
      <IconButton
        id="sidebar-feedback"
        aria-label="Pepwise 2.0 user feedback link"
        onClick={handleClick}
        // Apply fixed position styles directly to the IconButton container
        sx={{
          padding: 0,
          position: "fixed",
          bottom: 10, // Increased margin for better placement
          left: 10,
          zIndex: 1000,
          cursor: 'pointer',
          // Optional visual styling on the button itself
          bgcolor: 'white',
          // boxShadow: '0 0 8px rgba(255, 0, 0, 0.4)',
          // Default transition for smooth hover/exit
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s',

          // 2. Apply the animation when the 'animate' state is true
          ...(hasRouteChanged && {
            animation: `${pulseBounce} 1s 10s ease-in-out`, // 1.5s duration, infinite loop
          }),

          // Maintain hover effect
          '&:hover': {
            transform: 'scale(1.2)',
            // boxShadow: '0 0 12px rgba(57, 172, 231, 0.6)', 
            bgcolor: 'white',
            // Stop the pulsing animation on hover
            animation: 'none',
          },
        }}
      >
        {/* The ThumbsUpDownOutlined icon */}
        <ThumbsUpDownOutlined
          fontSize="large"
          sx={{
            color: '#39ACE7', // Using theme color for 'red'
          }}
        />
      </IconButton>
    </Tooltip>
  );
};

export default PepWiseFeedbackForm;